import { ThemeProvider, PINField, Box } from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Spinner from '../components/Spinner';
import { useGlobalState } from '../shared/state';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { Pages } from '../shared/pages';
import { ErrorType } from '../shared/types';
import { UtagHelper } from '../utils/utagHelper';
import { LogCannon } from '@ace-clublabs/log-cannon';
import { createZipCookieFromCode, checkExistingZipCookies, getDefaultZipForClub } from '../utils/createZipCookie';
import { IGetPBLDataResponse } from '../api/contracts';

const savePblDataAndNavigate = async (
  getPBLDataResponse: IGetPBLDataResponse,
  setManifestId: (value: string) => void,
  setPayByLinkData: (value: IGetPBLDataResponse) => void,
  navigate: ReturnType<typeof useNavigate>
) => {
  const { manifestId: _manifestId, clubCode } = getPBLDataResponse;
  if (getPBLDataResponse && _manifestId) {
    setManifestId(_manifestId);
    setPayByLinkData(getPBLDataResponse);

    const existingCookies = checkExistingZipCookies();
    if (!existingCookies.exists && clubCode) {
      try {
        const defaultZip = getDefaultZipForClub(clubCode);
        if (defaultZip) {
          await createZipCookieFromCode(defaultZip);
          LogCannon.blast('PBL: Created zip cookie', { clubCode, zipCode: defaultZip });
        }
      } catch (zipError: any) {
        LogCannon.blast('PBL: Failed to create zip cookie', zipError);
      }
    }

    navigate(Pages.PayByLink);
    return true;
  }

  return false;
};

export const PayByLinkAuthentication: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(true);
  const [pinRequired, setPinRequired] = useState(false);
  const [error, setError] = useGlobalState('error');
  const [customerId, setCustomerId] = useGlobalState('customerId');
  const [requestId, setEPCRequestId] = useGlobalState('epcRequestId');
  const [payByLinkData, setPayByLinkData] = useGlobalState('payByLinkData');
  const [, setManifestId] = useGlobalState('manifestId');
  const [sessionId] = useGlobalState('sessionId');

  const { customerId: customerIdFromParams, requestId: requestIdFromParams } = useParams<{
    customerId: string;
    requestId: string;
  }>();

  useEffect(() => {
    if (customerIdFromParams === customerId && requestIdFromParams === requestId && payByLinkData) {
      navigate(Pages.PayByLink);
    } else {
      setPayByLinkData(undefined);
      setError(undefined);
      setCustomerId(customerIdFromParams);
      setEPCRequestId(requestIdFromParams);
    }
  }, []);

  useEffect(() => {
    if (!pinRequired || !customerId || !sessionId) return;

    const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const isBackNavigation = navigationEntries.length > 0 && navigationEntries[0].type === 'back_forward';

    const smsVerificationTags = {
      channel: 'my account',
      subchannel1: 'sms',
      page: 'my account:sms:verification code',
      sessionid: sessionId,
      custID: customerId,
    };

    if (isBackNavigation) {
      UtagHelper.tag(smsVerificationTags);
    } else {
      (global as any).aceMediaTagValues = { ...smsVerificationTags };

      if (typeof (global as any).utag === 'undefined') {
        UtagHelper.loadUtagJs();
      } else {
        (global as any).utag.view(smsVerificationTags);
      }
    }
  }, [pinRequired, customerId, sessionId]);

  useEffect(() => {
    if (!customerIdFromParams || !requestIdFromParams) return;
    if (customerIdFromParams === customerId && requestIdFromParams === requestId && payByLinkData) return;

    (async () => {
      try {
        const getPBLDataRequest = { requestId: requestIdFromParams, custId: customerIdFromParams };
        const getPBLDataResponse = await sources.getPBLData(getPBLDataRequest);
        const success = await savePblDataAndNavigate(getPBLDataResponse, setManifestId, setPayByLinkData, navigate);
        if (!success) {
          setError({ errorMessage: '', errorType: ErrorType.PBL_ERROR });
          navigate(Pages.Error);
        }
      } catch (err: any) {
        if (err.type === ErrorType.PBL_PIN_REQUIRED) {
          setPinRequired(true);
          setLoading(false);
        } else if (err.type === ErrorType.PBL_EXPIRED) {
          setError({ errorMessage: '', errorType: ErrorType.PBL_EXPIRED });
          LogCannon.blast('Navigating to Error', {
            component: 'PayByLinkAuthentication',
            errorMessage: 'ERROR: PBL Link Expired',
            error: err,
          });
          navigate(Pages.Error);
        } else {
          setError({ errorMessage: '', errorType: ErrorType.PBL_ERROR });
          LogCannon.blast('Navigating to Error', {
            component: 'PayByLinkAuthentication',
            errorMessage: 'ERROR: Fail to Fetch PBL Data',
            error: err,
          });
          navigate(Pages.Error);
        }
      }
    })();
  }, [customerIdFromParams, requestIdFromParams]);

  const handleSubmit = async (pin: string) => {
    try {
      setLoading(true);
      const getPBLDataRequest = { requestId: requestId || '', custId: customerId || '', pin };
      const getPBLDataResponse = await sources.getPBLData(getPBLDataRequest);
      const success = await savePblDataAndNavigate(getPBLDataResponse, setManifestId, setPayByLinkData, navigate);
      setLoading(false);
      if (!success) {
        setError({ errorMessage: '', errorType: ErrorType.PBL_ERROR });
        navigate(Pages.Error);
      }
    } catch (err: any) {
      setLoading(false);
      if (err.type === ErrorType.PBL_INCORRECT_PIN) {
        setError({
          errorMessage: 'Verification failed. Please try inputting your code again.',
          errorType: ErrorType.PBL_INCORRECT_PIN,
        });
        UtagHelper.link({
          channel: 'my account',
          custID: customerId,
          error: 'my account:sms:verification code-verification failed',
          page: 'my account:sms:verification code',
          sessionid: sessionId,
          subchannel1: 'sms',
        });
        LogCannon.blast('ERROR: Incorrect PBL PIN');
      } else if (err.type === ErrorType.PBL_TOO_MANY_ATTEMPTS) {
        setError({
          errorMessage: "You've entered the wrong code too many times. Please request a new link from your agent.",
          errorType: ErrorType.PBL_TOO_MANY_ATTEMPTS,
        });
        UtagHelper.link({
          channel: 'my account',
          custID: customerId,
          error: 'my account:sms:verification code-too many attempts',
          page: 'my account:sms:verification code',
          sessionid: sessionId,
          subchannel1: 'sms',
        });
        LogCannon.blast('ERROR: Too Many Attempts for PBL Verification');
      } else if (err.type === ErrorType.PBL_EXPIRED) {
        setError({ errorMessage: '', errorType: ErrorType.PBL_EXPIRED });
        LogCannon.blast('Navigating to Error', {
          component: 'PayByLinkAuthentication',
          errorMessage: 'ERROR: PBL Link Expired',
          error: err,
        });
        navigate(Pages.Error);
      } else {
        setError({ errorMessage: '', errorType: ErrorType.PBL_ERROR });
        LogCannon.blast('Navigating to Error', {
          component: 'PayByLinkAuthentication',
          errorMessage: 'ERROR: Fail to Fetch PBL Data',
          error: err,
        });
        navigate(Pages.Error);
      }
    }
  };

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);

  if (pinRequired) {
    return (
      <>
        {isLoading && <Spinner />}
        <div id="pay-by-link-authentication" className="inner-container" hidden={isLoading}>
          <Header />
          <ThemeProvider>
            <Box id="pbl-pin">
              <PINField
                title="Enter your verification code"
                subtitle="Your agent will provide you with a 4-digit verification code. Your payment link will expire after 1 hour."
                onSubmit={handleSubmit}
                errorMessage={error?.errorMessage}
                disabled={error?.errorType === ErrorType.PBL_TOO_MANY_ATTEMPTS}
              />
            </Box>
          </ThemeProvider>
        </div>
      </>
    );
  }

  return <Spinner />;
};
